import { StrictMode, useState, useEffect, useCallback } from "react";
import { createRoot } from "react-dom/client";
import { Wizard } from "../wizard/Wizard";
import "../styles/wizard.css";

// Global open function so vanilla JS triggers can open the wizard
let globalOpen: (() => void) | null = null;

function WizardModal() {
  const [open, setOpen] = useState(false);

  const openWizard = useCallback(() => setOpen(true), []);

  useEffect(() => {
    globalOpen = openWizard;
    return () => { globalOpen = null; };
  }, [openWizard]);

  if (!open) return null;

  return (
    <div
      className="wizard-modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div className="wizard-modal" role="dialog" aria-modal="true">
        <Wizard onClose={() => setOpen(false)} />
      </div>
    </div>
  );
}

function bindTriggers() {
  document.querySelectorAll<HTMLElement>("[data-wizard-trigger]").forEach((el) => {
    if (el.dataset.wizardBound === "true") return;
    el.dataset.wizardBound = "true";
    el.addEventListener("click", (e) => {
      e.preventDefault();
      globalOpen?.();
    });
  });
}

function mount() {
  const host = document.getElementById("wizard-root");
  if (!host) return;

  // Only mount once
  if (host.dataset.wizardMounted === "true") return;
  host.dataset.wizardMounted = "true";

  createRoot(host).render(
    <StrictMode>
      <WizardModal />
    </StrictMode>,
  );

  // Bind existing triggers after a tick (React needs to render first)
  requestAnimationFrame(bindTriggers);
}

// Bind triggers on initial load and after Turbo navigations
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", mount);
} else {
  mount();
}

document.addEventListener("turbo:load", mount);
document.addEventListener("turbo:frame-render", bindTriggers);

// Also observe for dynamically added triggers (e.g. CMS content loaded via fetch)
const observer = new MutationObserver(bindTriggers);
observer.observe(document.body, { childList: true, subtree: true });
